import { useState, useEffect } from 'react';
import { LoginScreen } from '../components/shared/LoginScreen';
import { api } from '../lib/api';
import { AIChat } from '../components/shared/AIChat';
import { AIInsights } from '../components/shared/AIInsights';
import { Home, LogOut, Sun, Moon, ArrowLeft } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import PatronHub from './patron/PatronHub';
import PatronDashboard from './patron/Dashboard';
import PatronCA from './patron/CALive';
import PatronSaisie from './patron/SaisieCA';
import PatronSuivi from './patron/SuiviAnnuel';
import PatronFactures from './patron/Factures';
import PatronTraiteur from './patron/Traiteur';
import PatronCarte from './patron/FoodCostHub';
import CarteManager from './patron/CarteManager';
import PatronSalaires from './patron/Salaires';
import PatronFixes from './patron/ChargesFixes';
import PatronPrevisionnel from './patron/Previsionnel';
import PatronTresorerie from './patron/Tresorerie';
import PatronBanques from './patron/BanquePage';
import PatronRapprochement from './patron/Rapprochement';
import BudgetMensuel from './patron/BudgetMensuel';
import PatronOptimisation from './patron/Optimisation';
import PatronMercuriales from './patron/Mercuriales';
import PatronPerso from './patron/Perso';
import PatronTelegram from './patron/Telegram';
import PatronSettingsChef from './patron/SettingsChef';
import PatronConges from './patron/Conges';
import PatronBonsCadeaux from './patron/BonsCadeaux';
import PatronCongelateur from './patron/Congelateur';
import PatronPertes from './patron/Pertes';
import RoueGestion from './patron/RoueGestion';
import MenuDuJour from './patron/MenuDuJour';
import ClientsPage from './patron/ClientsPage';
import AdminResaPage from './patron/AdminResaPage';
import ResaSettings from './patron/ResaSettings';
import UserManagement from './patron/UserManagement';
import AdminCalendar from './patron/AdminCalendar';
import AdminFloorPlan from './patron/AdminFloorPlan';
import AdminCustomers from './patron/AdminCustomers';
import AdminMenu from './patron/AdminMenu';
import IACowork from './patron/IACowork';
import EmailManager from './patron/EmailManager';
import WhatsAppManager from './patron/WhatsAppManager';
import AdminSite from './patron/AdminSite';
import CrmPage from './patron/CrmPage';
import PropositionsPage from './patron/PropositionsPage';
import RdvCommercial from './patron/crm/RdvCommercial';
import CrmCampagne from './patron/crm/CrmCampagne';
import CrmInbox from './patron/crm/CrmInbox';
import CatalogueTraiteurPage from './patron/CatalogueTraiteurPage';
import EmailTemplatesLibrary from './patron/crm/EmailTemplatesLibrary';
import CalendrierCommercial from './patron/crm/CalendrierCommercial';
import GestionHub from './patron/GestionHub';
import IAKnowledgePage from './patron/IAKnowledgePage';
import TaskManager from './patron/TaskManager';
import QRCodeMenu from './patron/QRCodeMenu';
import RatiosCles from './patron/RatiosCles';

const PAGE_LABELS = {
  'hub': 'Accueil',
  'dashboard': 'Tableau de bord',
  'ratios': 'Ratios Cles',
  'ca': 'Chiffre d\'affaires',
  'saisie': 'Saisie CA',
  'suivi': 'Bilan annuel',
  'factures': 'Factures',
  'traiteur': 'Traiteur',
  'menu': 'Carte & Menu',
  'carte': 'Food Cost',
  'salaires': 'Salaires',
  'conges': 'Conges',
  'fixes': 'Charges fixes',
  'previsionnel': 'Previsionnel',
  'tresorerie': 'Tresorerie',
  'banques': 'Banques',
  'rapprochement': 'Rapprochement',
  'mercuriales': 'Mercuriales',
  'optimisation': 'Optim IA',
  'perso': 'Personnel',
  'telegram': 'Telegram',
  'settings-chef': 'Param. Chef',
  'bons-cadeaux': 'Bons Cadeaux',
  'congelateur': 'Congelateur',
  'pertes': 'Pertes',
  'budget': 'Budget',
  'roue': 'Jeu fidelite',
  'menu-du-jour': 'Menu du Jour',
  'clients': 'Clients',
  'reservations': 'Reservations',
  'resa-calendar': 'Calendrier',
  'resa-floor-plan': 'Plan de salle',
  'resa-customers': 'Clients resa',
  'resa-menu': 'Carte resto',
  'resa-settings': 'Param. resa',
  'users': 'Acces',
  'ia-cowork': 'IA Cowork',
  'messagerie': 'Messagerie',
  'whatsapp': 'WhatsApp',
  'admin-site': 'Site Web',
  'crm-dashboard': 'CRM',
  'crm-rapide': 'CRM Rapide',
  'crm-relances': 'Relances',
  'crm-pipeline': 'Pipeline CRM',
  'crm-signatures': 'Signatures',
  'crm-recherche': 'Recherche',
  'crm-sources': 'Sources',
  'crm-config': 'Config CRM',
  'crm-campagne': 'Campagnes',
  'crm-inbox': 'Boîte retour',
  'catalogue-traiteur': 'Catalogue Traiteur',
  'email-templates': 'Templates Email',
  'gestion': 'Gestion',
  'ia-knowledge': 'Base IA',
  'taches': 'Taches',
  'qrcode-menu': 'QR Code',
  'prop-dashboard': 'Propositions',
  'prop-pipeline': 'Pipeline',
  'prop-veille': 'Veille',
};

const PatronApp = () => {
  const [authenticated, setAuthenticated] = useState(() =>
    sessionStorage.getItem('patron_auth') === 'true'
  );
  const [user, setUser] = useState(() => {
    const role = sessionStorage.getItem('user_role');
    const email = sessionStorage.getItem('user_email');
    const nom = sessionStorage.getItem('user_nom');
    return role ? { role, email, nom } : null;
  });
  // Detect restricted mode from URL params (?mode=commercial or ?mode=scan)
  const [pwaMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') || null;
  });
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    if (mode === 'commercial') return 'rdv-commercial';
    if (mode === 'scan') return 'menu';
    // Tab set from the public hub (AdminHub) via sessionStorage
    const patronTab = sessionStorage.getItem('patron_tab');
    if (patronTab) {
      sessionStorage.removeItem('patron_tab');
      return patronTab;
    }
    const pwaTarget = sessionStorage.getItem('pwa_target');
    if (pwaTarget) {
      sessionStorage.removeItem('pwa_target');
      return pwaTarget;
    }
    const hash = window.location.hash.replace('#', '');
    return hash && hash !== '' ? hash : 'hub';
  });
  const [checkingAuth, setCheckingAuth] = useState(true);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
        setAuthenticated(true);
        sessionStorage.setItem('patron_auth', 'true');
        sessionStorage.setItem('user_role', res.data.role);
        sessionStorage.setItem('user_email', res.data.email);
        sessionStorage.setItem('user_nom', res.data.nom || '');
      } catch {
        sessionStorage.removeItem('patron_auth');
        setAuthenticated(false);
      }
      setCheckingAuth(false);
    };
    checkAuth();
  }, []);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setAuthenticated(true);
  };

  if (checkingAuth) {
    return <div className="min-h-screen bg-[rgb(var(--lm-bg))] flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-[rgb(var(--lm-gold))] border-t-transparent rounded-full" />
    </div>;
  }

  if (!authenticated) {
    return <LoginScreen onSuccess={handleAuthSuccess} />;
  }

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('patron_auth');
    sessionStorage.removeItem('user_role');
    sessionStorage.removeItem('user_email');
    sessionStorage.removeItem('user_nom');
    setAuthenticated(false);
    setUser(null);
  };

  const isOnHub = activeTab === 'hub' || activeTab === 'commercial-hub' || activeTab === 'scan-hub';

  const COMMERCIAL_TABS = [
    { id: 'rdv-commercial', label: 'RDV Commercial', desc: 'Premier contact guide', icon: '📋' },
    { id: 'calendrier-commercial', label: 'Calendrier', desc: 'Vue mensuelle evenements', icon: '📅' },
    { id: 'prop-dashboard', label: 'Propositions', desc: 'Devis & suivi commercial', icon: '📄' },
    { id: 'crm-campagne', label: 'Campagnes Email', desc: 'Envoi en masse', icon: '📨' },
    { id: 'crm-inbox', label: 'Boîte retour', desc: 'Réponses · Désinscriptions', icon: '📩' },
    { id: 'crm-dashboard', label: 'Prospection', desc: 'CRM B2B complet', icon: '🎯' },
    { id: 'prop-pipeline', label: 'Pipeline', desc: 'Kanban propositions', icon: '📊' },
  ];

  const SCAN_TABS = [
    { id: 'menu', label: 'Carte & Menu', desc: 'Gestion de la carte', icon: '📖' },
    { id: 'menu-du-jour', label: 'Menu du Jour', desc: 'Menu hebdomadaire', icon: '📅' },
    { id: 'carte', label: 'Food Cost', desc: 'Cout des plats', icon: '💰' },
  ];

  // Restrict tab navigation in restricted modes
  const handleSetTab = (tab) => {
    const commercialAllowed = ['rdv-commercial', 'calendrier-commercial', 'prop-dashboard', 'crm-campagne', 'crm-inbox', 'catalogue-traiteur', 'crm-dashboard', 'crm-rapide', 'crm-relances', 'crm-pipeline', 'crm-ia', 'crm-rayon', 'crm-matin', 'crm-signatures', 'crm-recherche', 'crm-sources', 'crm-config', 'crm-export', 'prop-pipeline', 'prop-veille', 'commercial-hub'];
    const scanAllowed = ['menu', 'menu-du-jour', 'carte', 'scan-hub'];
    if (pwaMode === 'commercial' && !commercialAllowed.includes(tab)) {
      setActiveTab('commercial-hub');
      return;
    }
    if (pwaMode === 'scan' && !scanAllowed.includes(tab)) {
      setActiveTab('scan-hub');
      return;
    }
    setActiveTab(tab);
  };

  const renderPage = () => {
    // Commercial restricted hub
    if (pwaMode === 'commercial' && (activeTab === 'hub' || activeTab === 'commercial-hub')) {
      return (
        <div className="space-y-6">
          <div className="text-center py-4">
            <h1 className="text-xl font-bold text-[rgb(var(--lm-text))]" style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.1em' }}>LE MAS</h1>
            <p className="text-xs text-[rgb(var(--lm-text-muted))]">Espace Commercial</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {COMMERCIAL_TABS.map(tab => (
              <button key={tab.id} onClick={() => handleSetTab(tab.id)}
                className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl p-5 text-center hover:shadow-md transition-all"
                data-testid={`commercial-tab-${tab.id}`}>
                <span className="text-2xl block mb-2">{tab.icon}</span>
                <p className="text-sm font-bold text-[rgb(var(--lm-text))]">{tab.label}</p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))] mt-1">{tab.desc}</p>
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Scan restricted hub
    if (pwaMode === 'scan' && (activeTab === 'hub' || activeTab === 'scan-hub')) {
      return (
        <div className="space-y-6">
          <div className="text-center py-4">
            <h1 className="text-xl font-bold text-[rgb(var(--lm-text))]" style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.1em' }}>LE MAS</h1>
            <p className="text-xs text-[rgb(var(--lm-text-muted))]">Scan & Menu</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {SCAN_TABS.map(tab => (
              <button key={tab.id} onClick={() => handleSetTab(tab.id)}
                className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl p-5 text-center hover:shadow-md transition-all"
                data-testid={`scan-tab-${tab.id}`}>
                <span className="text-2xl block mb-2">{tab.icon}</span>
                <p className="text-sm font-bold text-[rgb(var(--lm-text))]">{tab.label}</p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))] mt-1">{tab.desc}</p>
              </button>
            ))}
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'hub': return <PatronHub setActiveTab={setActiveTab} />;
      case 'dashboard': return <PatronDashboard setActiveTab={setActiveTab} />;
      case 'ratios': return <RatiosCles />;
      case 'ca': return <PatronCA />;
      case 'saisie': return <PatronSaisie />;
      case 'suivi': return <PatronSuivi />;
      case 'factures': return <PatronFactures />;
      case 'traiteur': return <PatronTraiteur />;
      case 'menu': return <CarteManager />;
      case 'carte': return <PatronCarte />;
      case 'salaires': return <PatronSalaires />;
      case 'conges': return <PatronConges />;
      case 'fixes': return <PatronFixes />;
      case 'previsionnel': return <PatronPrevisionnel />;
      case 'tresorerie': return <PatronTresorerie />;
      case 'banques': return <PatronBanques />;
      case 'rapprochement': return <PatronRapprochement />;
      case 'mercuriales': return <PatronMercuriales />;
      case 'optimisation': return <PatronOptimisation />;
      case 'perso': return <PatronPerso />;
      case 'telegram': return <PatronTelegram />;
      case 'settings-chef': return <PatronSettingsChef />;
      case 'bons-cadeaux': return <PatronBonsCadeaux />;
      case 'congelateur': return <PatronCongelateur />;
      case 'pertes': return <PatronPertes />;
      case 'budget': return <BudgetMensuel />;
      case 'roue': return <RoueGestion />;
      case 'menu-du-jour': return <MenuDuJour />;
      case 'clients': return <ClientsPage />;
      case 'reservations': return <AdminResaPage />;
      case 'resa-calendar': return <AdminCalendar />;
      case 'resa-floor-plan': return <AdminFloorPlan />;
      case 'resa-customers': return <AdminCustomers />;
      case 'resa-menu': return <AdminMenu />;
      case 'resa-settings': return <ResaSettings />;
      case 'users': return <UserManagement />;
      case 'ia-cowork': return <IACowork />;
      case 'messagerie': return <EmailManager />;
      case 'whatsapp': return <WhatsAppManager />;
      case 'admin-site': return <AdminSite />;
      case 'crm-dashboard': return <CrmPage initialTab="dashboard" />;
      case 'crm-rapide': return <CrmPage initialTab="rapide" />;
      case 'crm-relances': return <CrmPage initialTab="relances" />;
      case 'crm-pipeline': return <CrmPage initialTab="pipeline" />;
      case 'crm-signatures': return <CrmPage initialTab="signatures" />;
      case 'crm-recherche': return <CrmPage initialTab="recherche" />;
      case 'crm-sources': return <CrmPage initialTab="sources" />;
      case 'crm-config': return <CrmPage initialTab="config" />;
      case 'gestion': return <GestionHub setActiveTab={setActiveTab} />;
      case 'ia-knowledge': return <IAKnowledgePage />;
      case 'taches': return <TaskManager />;
      case 'qrcode-menu': return <QRCodeMenu />;
      case 'prop-dashboard': return <PropositionsPage />;
      case 'prop-pipeline': return <PropositionsPage />;
      case 'prop-veille': return <PropositionsPage />;
      case 'rdv-commercial': return <RdvCommercial />;
      case 'calendrier-commercial': return <CalendrierCommercial onOpenRdv={(id) => { /* TODO: open rdv by id */ }} />;
      case 'crm-campagne': return <CrmCampagne />;
      case 'crm-inbox': return <CrmInbox />;
      case 'catalogue-traiteur': return <CatalogueTraiteurPage />;
      case 'email-templates': return <EmailTemplatesLibrary />;
      default: return <PatronHub setActiveTab={setActiveTab} />;
    }
  };

  return (
    <div className="min-h-screen bg-[rgb(var(--lm-bg))] transition-colors duration-300">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[rgb(var(--lm-card))]/95 backdrop-blur-md border-b border-[rgb(var(--lm-border))]/30">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            {!isOnHub && (
              <button
                onClick={() => {
                  if (pwaMode === 'commercial') setActiveTab('commercial-hub');
                  else if (pwaMode === 'scan') setActiveTab('scan-hub');
                  else setActiveTab('hub');
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[rgb(var(--lm-gold))]/10 hover:bg-[rgb(var(--lm-gold))]/20 transition-colors"
                data-testid="back-to-hub-btn"
              >
                <ArrowLeft size={18} className="text-[rgb(var(--lm-gold))]" />
                <span className="text-sm font-medium text-[rgb(var(--lm-gold))]">Accueil</span>
              </button>
            )}
            {isOnHub ? (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[rgb(var(--lm-gold))]/15 flex items-center justify-center">
                  <Home size={16} className="text-[rgb(var(--lm-gold))]" />
                </div>
                <span className="text-lg font-bold text-[rgb(var(--lm-text))]" style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.1em' }}>
                  LE MAS
                </span>
              </div>
            ) : (
              <span className="text-base font-semibold text-[rgb(var(--lm-text))]">
                {PAGE_LABELS[activeTab] || activeTab}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-[rgb(var(--lm-surface-elevated))] transition-colors"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun size={16} className="text-[rgb(var(--lm-text-muted))]" /> : <Moon size={16} className="text-[rgb(var(--lm-text-muted))]" />}
            </button>
            <button
              onClick={handleLogout}
              className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-red-500/10 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={16} className="text-[rgb(var(--lm-text-muted))]" />
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto p-4 md:p-6 lg:p-8">
        {!isOnHub && <AIInsights page={activeTab} role="patron" />}
        {renderPage()}
      </main>

      <AIChat role="patron" page={activeTab} />
    </div>
  );
};

export default PatronApp;
